import {
  BarChart3,
  Check,
  CircleDot,
  ClipboardCheck,
  GitMerge,
  Megaphone,
  MessageSquareMore,
  PanelsTopLeft,
  Settings2,
  Target,
  Users,
} from 'lucide-react';

const shell = 'px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14';
const container = 'mx-auto w-full max-w-[1180px]';
const eyebrow = 'text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]';
const heading = 'text-[30px] font-bold leading-[1.04] tracking-[-0.035em] text-[#0D2249] sm:text-[38px] md:text-[48px]';

export function FounderSection() {
  return (
    <section className={`${shell} bg-[#F2F1EF]`} data-testid="founder-section">
      <div className={container} data-einblenden>
        <div className="grid overflow-hidden rounded-[30px] bg-[#0D2249] text-white lg:grid-cols-[0.78fr_1.22fr]">
          <div className="relative min-h-[320px] overflow-hidden bg-[#CC3366] p-8 sm:p-10">
            <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full border-[34px] border-white/10" aria-hidden="true" />
            <img
              src="/images/cartoon-dennis.webp"
              alt="Dennis, Gründer und Entwickler bei Make It Media"
              className="absolute inset-x-0 bottom-0 mx-auto h-[88%] w-auto object-contain object-bottom"
            />
            <span className="absolute bottom-5 left-5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-[#0D2249] shadow-lg">Dennis · Systeme & Entwicklung</span>
          </div>
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#F3A7C1]">Wer dahintersteht</p>
            <h2 className="mt-4 text-[30px] font-bold leading-[1.04] tracking-[-0.035em] sm:text-[38px] md:text-[46px]">
              Kein anonymes Agenturteam. Die Menschen, die verkaufen, bauen auch das System.
            </h2>
            <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-white/72">
              Dennis entwickelt die technische Infrastruktur und verbindet Marketingdaten mit dem tatsächlichen Praxisablauf. Celina verantwortet Positionierung, Kampagnen und Gestaltung. Seit 2019 bauen wir Make It Media gemeinsam auf und begleiten die Umsetzung persönlich.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2" data-folge>
              <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-4">
                <Settings2 className="h-5 w-5 text-[#F3A7C1]" />
                <p className="mt-3 text-sm font-semibold">Dennis</p>
                <p className="mt-1 text-xs leading-relaxed text-white/55">Entwicklung, Automatisierung und Messbarkeit</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-4">
                <Megaphone className="h-5 w-5 text-[#F3A7C1]" />
                <p className="mt-3 text-sm font-semibold">Celina</p>
                <p className="mt-1 text-xs leading-relaxed text-white/55">Marketing, Positionierung und Design</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PROZESS = [
  ['01', 'Auswertung', 'Wir berechnen aus Ihren vorhandenen Terminen, wo Beratungen verloren gehen.'],
  ['02', 'Priorität', 'Wir wählen die Behandlungen, Kapazitäten und Zielgruppen aus, die wirklich wachsen sollen.'],
  ['03', 'Aufbau', 'Wir verbinden Kampagne, Anfrage, Qualifizierung, Termin und Nachverfolgung.'],
  ['04', 'Betrieb', 'Ihr Team erhält klare nächste Schritte, während wir Nachfrage und Abläufe steuern.'],
  ['05', 'Verbesserung', 'Wir messen Behandlungen statt nur Leads und verbessern den Engpass mit der größten Wirkung.'],
];

export function ProcessSection() {
  return (
    <section className={shell} data-testid="process-section">
      <div className={container} data-einblenden>
        <div className="max-w-3xl">
          <p className={eyebrow}>Der Ablauf</p>
          <h2 className={`mt-4 ${heading}`}>Vom ersten Datenpunkt bis zum laufenden Patientengewinnungssystem.</h2>
          <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-[#3B4962] md:text-lg">Sie kaufen keine lose Kampagne. Wir bauen den Ablauf mit Ihnen auf, führen ihn ein und verbessern ihn anhand echter Behandlungsergebnisse.</p>
        </div>
        <ol className="mt-12 grid gap-3 lg:grid-cols-5">
          {PROZESS.map(([nummer, titel, text_], index) => (
            <li key={titel} className="group relative rounded-[24px] border border-[#0D2249]/10 bg-white p-5 shadow-[0_18px_50px_-38px_rgba(13,34,73,0.5)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-34px_rgba(13,34,73,0.55)]">
              <span className="text-xs font-semibold tracking-[0.12em] text-[#CC3366]">{nummer}</span>
              <h3 className="mt-8 text-lg font-bold tracking-[-0.025em] text-[#0D2249]">{titel}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#68748A]">{text_}</p>
              {index < PROZESS.length - 1 && <GitMerge className="absolute -right-3 top-6 z-10 hidden h-5 w-5 rotate-[-90deg] rounded-full bg-[#F2F1EF] p-1 text-[#CC3366] lg:block" aria-hidden="true" />}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const VERGLEICH = [
  { icon: Megaphone, title: 'Klassische Agentur', points: ['sieht Klicks und Anfragen', 'endet meist vor dem Beratungstermin', 'kann Behandlungen selten zuordnen'] },
  { icon: PanelsTopLeft, title: 'Praxissoftware', points: ['sieht Termine und Dokumentation', 'kennt die Kampagne davor nicht', 'erzeugt keine Nachfrage'] },
  { icon: GitMerge, title: 'Unser verbundenes System', points: ['führt Nachfrage bis zur Behandlung', 'verbindet Team, Termine und Follow-up', 'zeigt, welche Maßnahme Umsatz erzeugt'], featured: true },
];

export function ComparisonSection() {
  return (
    <section className={`${shell} bg-[#F2F1EF]`} data-testid="comparison-section">
      <div className={container} data-einblenden>
        <div className="max-w-3xl">
          <p className={eyebrow}>Der Vergleich</p>
          <h2 className={`mt-4 ${heading}`}>Nicht noch ein Werkzeug. Die Verbindung zwischen den Werkzeugen.</h2>
        </div>
        <div className="mt-10 grid gap-4 lg:grid-cols-3" data-folge>
          {VERGLEICH.map(({ icon: Icon, title, points, featured }) => (
            <article key={title} className={`rounded-[26px] border p-6 ${featured ? 'border-[#CC3366] bg-[#0D2249] text-white shadow-[0_30px_80px_-42px_rgba(13,34,73,0.85)]' : 'border-[#0D2249]/10 bg-white text-[#0D2249]'}`}>
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${featured ? 'bg-[#CC3366] text-white' : 'bg-[#F2F1EF] text-[#0D2249]'}`}><Icon className="h-5 w-5" /></div>
              <h3 className="mt-7 text-xl font-bold tracking-[-0.03em]">{title}</h3>
              <ul className="mt-5 space-y-3">
                {points.map((point) => <li key={point} className={`flex gap-2.5 text-sm leading-relaxed ${featured ? 'text-white/72' : 'text-[#68748A]'}`}><CircleDot className={`mt-1 h-3.5 w-3.5 shrink-0 ${featured ? 'text-[#F3A7C1]' : 'text-[#CC3366]'}`} />{point}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const ENTHALTEN = [
  { icon: Target, title: 'Nachfrage', text: 'Positionierung, Kampagnen und Inhalte für die Behandlungen, die Sie gezielt auslasten wollen.' },
  { icon: Users, title: 'Conversion', text: 'Qualifizierung, klare Übergaben und eine Buchungsstrecke, die Interessenten in Beratungen führt.' },
  { icon: MessageSquareMore, title: 'Follow-up', text: 'Verbindliche nächste Schritte, Erinnerungen und Nachverfolgung, damit keine Beratung still verschwindet.' },
  { icon: BarChart3, title: 'Messbarkeit', text: 'Auswertung vom Erstkontakt bis zur Behandlung, inklusive Quelle, Status und wirtschaftlichem Ergebnis.' },
];

export function IncludedSection() {
  return (
    <section className={shell} data-testid="included-section">
      <div className={container} data-einblenden>
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-folge>
          <div>
            <p className={eyebrow}>Was enthalten ist</p>
            <h2 className={`mt-4 ${heading}`}>Vier Bestandteile. Ein gemeinsames Ergebnis.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-[#3B4962]">Jeder Bestandteil wird für Ihre Praxis eingerichtet und zusammen mit Ihrem Team in einen nutzbaren Ablauf übersetzt.</p>
          </div>
          <div className="rounded-[26px] bg-[#CC3366] p-6 text-white sm:p-8">
            <ClipboardCheck className="h-7 w-7" />
            <p className="mt-5 text-2xl font-bold leading-tight tracking-[-0.035em]">Wir liefern nicht mehr Aktivität. Wir schließen den Weg zur Behandlung.</p>
          </div>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2" data-folge>
          {ENTHALTEN.map(({ icon: Icon, title, text }) => (
            <article key={title} className="group rounded-[26px] border border-[#0D2249]/10 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-[#CC3366]/35 hover:shadow-[0_24px_60px_-40px_rgba(13,34,73,0.55)]">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F2F1EF] text-[#CC3366] transition group-hover:bg-[#CC3366] group-hover:text-white"><Icon className="h-5 w-5" /></div>
              <h3 className="mt-6 text-xl font-bold tracking-[-0.03em] text-[#0D2249]">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#68748A]">{text}</p>
              <p className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-[#CC3366]"><Check className="h-3.5 w-3.5" /> Für Ihre Praxis eingerichtet</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
